import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  CheckboxListItemData,
  SearchableCheckboxList,
} from '@/experiences/sales-portal/components/searchable-checkbox-list';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { QuoteItemSearchResult } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/loaders/use-quote-item-search-loader';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

type SearchResultItem = CheckboxListItemData<QuoteItemSearchResult>;

interface QuoteItemSearchViewProps {
  hasError: boolean;
  isSearching: boolean;
  onSearch: (text: string) => void;
  onToggleItem: (item: SearchResultItem) => void;
  results: QuoteItemSearchResult[];
  selectedKeys: Set<string>;
}

/**
 * Presentational catalog-item search combobox: shows the input, the dropdown
 * with results, and an empty/loading/error state. Receives `results` /
 * `isSearching` / `hasError` / `selectedKeys` from its container; owns only
 * the local UI state (dropdown open/closed, current input value).
 */
export const QuoteItemSearchView: React.FC<QuoteItemSearchViewProps> = ({
  hasError,
  isSearching,
  onSearch,
  onToggleItem,
  results,
  selectedKeys,
}) => {
  const [styles, theme] = useStyles();
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const items = useMemo<SearchResultItem[]>(
    () => results.map((r) => ({ ...r, key: r.variantPackId })),
    [results],
  );

  const handleSearch = useCallback(
    (text: string) => {
      setSearchText(text);
      // Open state is gated on trimmed length — whitespace-only shouldn't
      // open an empty dropdown. The loader normalizes the query downstream.
      setIsOpen(text.trim().length > 0);
      onSearch(text);
    },
    [onSearch],
  );

  const handleClose = useCallback(() => setIsOpen(false), []);

  // Refocusing the input with text already in it should bring the prior
  // results back — clicking outside hides the dropdown but doesn't clear
  // the search.
  const handleSearchInputFocus = useCallback(() => {
    if (searchText.length > 0) {
      setIsOpen(true);
    }
  }, [searchText]);

  // Footer shows the "Searching..." affordance during follow-up searches so the rep gets feedback while prior results stay on screen; otherwise hidden.
  const searchingLabel = i18n.t(
    'sales_portal.price_quotes.searching_the_catalog',
  );
  const footerText = isSearching ? searchingLabel : undefined;

  const emptyStateMessage = isSearching
    ? searchingLabel
    : hasError
      ? i18n.t('sales_portal.price_quotes.search_error')
      : i18n.t('sales_portal.price_quotes.no_search_results');

  const listEmptyComponent = useMemo(
    () => (
      <View style={styles.emptyState}>
        <Typography color={theme.sk.color.text.tertiary} variant="paragraph-md">
          {emptyStateMessage}
        </Typography>
      </View>
    ),
    [emptyStateMessage, styles.emptyState, theme.sk.color.text.tertiary],
  );

  const renderItemContent = useCallback(
    (item: SearchResultItem) => {
      const codeAndPackSize = [item.itemCode, item.itemPackSize]
        .filter(Boolean)
        .join(' • ');
      return (
        <View style={styles.itemRow}>
          <View style={styles.itemTopRow}>
            <Typography
              color={theme.sk.color.text.primary}
              flexShrink={1}
              numberOfLines={1}
              variant="paragraph-md-600"
            >
              {item.itemName}
            </Typography>
            <Typography
              color={theme.sk.color.text.primary}
              flexShrink={0}
              variant="paragraph-md-600"
            >
              {formatCurrency(item.packPriceInMicros, item.currencyCode)}
            </Typography>
          </View>
          {item.itemDescription ? (
            <Typography
              color={theme.sk.color.text.secondary}
              numberOfLines={1}
              variant="paragraph-sm"
            >
              {item.itemDescription}
            </Typography>
          ) : null}
          {codeAndPackSize ? (
            <Typography
              color={theme.sk.color.text.secondary}
              numberOfLines={1}
              variant="paragraph-sm"
            >
              {codeAndPackSize}
            </Typography>
          ) : null}
        </View>
      );
    },
    [styles, theme],
  );

  return (
    <SearchableCheckboxList<QuoteItemSearchResult>
      footerText={footerText}
      getAccessibilityLabel={getRowAccessibilityLabel}
      isOpen={isOpen}
      items={items}
      listEmptyComponent={listEmptyComponent}
      onClose={handleClose}
      onSearchChange={handleSearch}
      onSearchInputFocus={handleSearchInputFocus}
      onToggleItem={onToggleItem}
      renderItem={renderItemContent}
      searchPlaceholder={i18n.t('sales_portal.price_quotes.search_placeholder')}
      searchText={searchText}
      selectedKeys={selectedKeys}
    />
  );
};

const getRowAccessibilityLabel = (item: SearchResultItem): string =>
  item.itemCode ? `${item.itemName}, ${item.itemCode}` : item.itemName;

const useStyles = createStyles((theme) => ({
  emptyState: {
    alignItems: 'center',
    paddingVertical: theme.sk.spacing.spaceLarge,
  },
  itemRow: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  itemTopRow: {
    alignItems: 'center',
    flexDirection: 'row',
    // Guarantees a gutter between the name and the price even when itemName
    // fills the row.
    gap: theme.sk.spacing.spaceMedium,
    justifyContent: 'space-between',
  },
}));
